import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as PR from "../../prime-react/index";
import { noGroupImage, profileIcon } from "../../assets/images/index";
import { getAlertConversation, updateAlertConversation, createAlertConversation, updateAlertLasttRead } from '../../services/api';
import { downloadAttachment } from "../../utils/downloadAttachment";
import { chatCount, chatFileTypesConfig, cleanData, dateTimeFormat, dompurifyText, editorConfig, fileUploadLimit, getPlainText, processHtmlLinks, textSanitize, trimFileName, validationMessages, remainingCharacters } from '../../utils/reuse';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import useQuillTooltipPositioning from "../../utils/useQuillTooltipPositioning";
import useSenderImages from "./useSenderImages";
import { useToast } from '../../context/ToastContext';
import './ChatDialog.scss';

const ChatDialog = ({
  visible,
  onHide,
  chatInfo,
  conversationClicked = true,
  groupNamesData,
  onUpdateChatReadSuccess
}) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const sessionData = useSelector((state) => state.auth.sessionData);
  const isMember = useSelector((state) => state.auth.role) === "member";
  const isAnalyst = useSelector((state) => state.auth.role) === "analyst";
  const userName = sessionData.companyName;

  const headers = useMemo(() => {
    return { sessionid: sessionData.sessionId };
  }, [sessionData.sessionId]);

  const multipartheaders = useMemo(() => {
    return { sessionid: sessionData.sessionId, "Content-Type": "multipart/form-data" };
  }, [sessionData.sessionId]);

  const [chatData, setChatData] = useState([]);
  const [chatParticipants, setChatParticipants] = useState([]);
  const [chatInfoState, setChatInfoState] = useState(chatInfo || {});
  const [chatLoading, setChatLoading] = useState(false);
  const [isChatPopupOpen, setChatPopupOpen] = useState(false);
  const [isAlertsUpdated, setIsAlertsUpdated] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Sync initial chatInfo prop 
  useEffect(() => {
    if (chatInfo) {
      setChatInfoState((prev) => {
        const isSameChat = Boolean(chatInfo?.alertID && prev?.alertID === chatInfo?.alertID && prev?.groupID === chatInfo?.groupID);
        return {
          ...chatInfo,
          conversationID: chatInfo.conversationID || (isSameChat ? prev?.conversationID : ''),
          alertDescription: chatInfo.alertDescription || (isSameChat ? prev?.alertDescription : ''),
          responseDescription: chatInfo.responseDescription || (isSameChat ? prev?.responseDescription : ''),
        };
      });
    }
  }, [chatInfo]);

  const getChatConversation = useCallback((id, groupID) => {
    setChatLoading(true);
    getAlertConversation(id, groupID, headers, dispatch, (response) => {
      setChatPopupOpen(true);
      if (response.data?.messages) {
        const sortedMessages = response.data.messages.length > 0 
          ? response.data.messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
          : [];
        setChatData(sortedMessages);
        setChatInfoState((prev) => ({
          ...prev,
          conversationID: response.data.conversationID,
          alertDescription: response.data.alertDescription,
          responseDescription: response.data.responseDescription || prev.responseDescription,
        }));
        setChatParticipants(response.data.participants);

        // to update chatRead only when readAt is null for last sender's message
        const lastSenderMessage = response.data.messages
          .filter((message) => message.senderID !== sessionData.groupID)
          .pop();
        if (lastSenderMessage && !lastSenderMessage.readAt) {
          setIsAlertsUpdated(true);
        }
      } else {
        setChatData([]);
        setChatParticipants([]);
        setChatInfoState((prev) => ({
          ...prev,
          conversationID: prev.groupID,
          alertDescription: response?.data?.alertDescription,
          responseDescription: response?.data?.responseDescription,
        }));
      }
      setChatLoading(false);
    });
  }, [dispatch, headers, sessionData.groupID]);

  // Fetch conversation on visible or when target changes
  useEffect(() => {
    if (visible && chatInfo?.alertID && chatInfo?.groupID) {
      getChatConversation(chatInfo.alertID, chatInfo.groupID);
    }
  }, [visible, chatInfo?.alertID, chatInfo?.groupID, getChatConversation]);

  const currentConversationID = useMemo(
    () => chatInfoState?.conversationID,
    [chatInfoState]
  );

  const updateChatRead = (chatID, formData) => {
    if (chatID) {
      updateAlertLasttRead(chatID, formData, multipartheaders, dispatch, (response) => {
        if (response.result === "SUCCESS") {
          setIsAlertsUpdated(false);
          setChatLoading(false);
          if (onUpdateChatReadSuccess) {
            onUpdateChatReadSuccess(chatInfoState, response.data);
          }
        }
      });
    }
  };

  useEffect(() => {
    if (currentConversationID && isAlertsUpdated) {
      const payload = {
        isRead: true,
        includeActiveConversation: conversationClicked ? true : false,
      };
      const formData = new FormData();
      formData.append("data", JSON.stringify(payload));
      updateChatRead(currentConversationID, formData);
    }
    // eslint-disable-next-line
  }, [isAlertsUpdated, currentConversationID, conversationClicked]);

  const chatParticipantsMap = useMemo(() => {
    return chatParticipants.reduce((acc, participant) => {
      acc[participant.ID] = participant;
      return acc;
    }, {});
  }, [chatParticipants]);

  const getChatUserName = (senderId) => {
    return chatParticipantsMap[senderId]?.name || '';
  };

  const shouldDisplaySenderImage = useCallback(
    (senderId) => chatParticipantsMap[senderId]?.name !== userName,
    [chatParticipantsMap, userName]
  );

  const chatMessagesWithImages = useMemo(() => {
    const messagesWithImages = chatData.map((message) => ({
      ...message,
      image: chatParticipantsMap[message?.senderID]?.image,
    }));
    return messagesWithImages;
  }, [chatData, chatParticipantsMap]);

  const chatMessagesWithVisibleSenderImages = useMemo(() => {
    return chatMessagesWithImages.filter((message) =>
      shouldDisplaySenderImage(message?.senderID)
    );
  }, [chatMessagesWithImages, shouldDisplaySenderImage]);

  const getSenderImages = useMemo(() => {
    let images = chatMessagesWithVisibleSenderImages;
    if (chatInfoState?.conversationID && chatMessagesWithVisibleSenderImages.length === 0) {
      images = isMember
        ? [{ senderID: 1, image: chatParticipantsMap[1]?.image }]
        : [{ senderID: chatInfoState.groupID, image: chatInfoState.groupImage || groupNamesData?.[chatInfoState.groupID]?.groupImage }];
    }
    return images;
  }, [chatInfoState, chatMessagesWithVisibleSenderImages, chatParticipantsMap, groupNamesData, isMember]);

  const senderImages = useSenderImages({
    messages: getSenderImages,
    senderIdKey: "senderID",
    senderImageKey: "image",
    headers,
    dispatch,
    resetKey: chatInfoState?.conversationID,
  });

  const updateChat = (chatID, formData) => {
    setChatLoading(true);
    updateAlertConversation(chatID, formData, multipartheaders, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        getChatConversation(chatInfoState.alertID, chatInfoState.groupID);
        setChatLoading(false);
        formik.resetForm();
      } else {
        setChatLoading(false);
        const error = response.error;
        setErrorMessage(error?.errorMsg || error?.summary);
        setTimeout(() => {
          setErrorMessage('');
        }, 5000);
      }
    });
  };

  const createChat = (alertID, groupID, formData) => {
    setChatLoading(true);
    createAlertConversation(alertID, groupID, formData, multipartheaders, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        getChatConversation(chatInfoState.alertID, chatInfoState.groupID);
        setChatLoading(false);
        formik.resetForm();
      } else {
        setChatLoading(false);
        const error = response.error;
        setErrorMessage(error?.errorMsg || error?.summary);
        setTimeout(() => {
          setErrorMessage('');
        }, 5000);
      }
    });
  };

  const handleSubmit = (formValues) => {
    let obj = {};
    obj.content = textSanitize(formValues.content);
    const refinedObj = cleanData(obj);
    const formData = new FormData();
    if (refinedObj && Object.keys(refinedObj).length > 0) {
      formData.append('data', JSON.stringify(refinedObj)); 
    }
    if (formValues.attachments && formValues.attachments.length > 0) {
      formValues.attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }

    if (chatData?.length === 0) {
      createChat(chatInfoState.alertID, chatInfoState.groupID, formData);
    } else {
      updateChat(chatInfoState.conversationID, formData);
    }
    setErrorMessage('');
  };

  const initialValues = {
    content: '',
    attachments: [],
  };

  const validationSchema = () => {
    return Yup.object().shape({
      attachments: Yup.array()
        .nullable()
        .test("fileSize", validationMessages.sizeExceed, (value) => {
          return !value || value.every(file => file.size <= fileUploadLimit.value);
        })
        .test("fileType", validationMessages.allTypes, (value) => {
          return !value || value.every(file => chatFileTypesConfig.includes(file.type));
        }),
    });
  };       

  const formik = useFormik({
    initialValues,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  });

  const fetchFileDetails = (file) => {
    downloadAttachment({
      file,
      id: chatInfoState?.legacyID || chatInfoState?.alertID,
      headers,
      dispatch,
      showToast,
    });
  };

  const handleDeleteAttachment = (index) => {
    const updatedAttachments = [...formik.values.attachments];
    updatedAttachments.splice(index, 1);
    formik.setFieldValue('attachments', updatedAttachments);
  };

  useQuillTooltipPositioning();

  const messagesEndRef = useRef(null);
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      if (chatData && chatData.length > 1) {
        const isSmoothScrollSupported = 'scrollBehavior' in document.documentElement.style;
        if (isSmoothScrollSupported) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } else {
          messagesEndRef.current.scrollIntoView(false);
        }
      }
    }
  }, [chatData]);

  useEffect(() => {
    if (visible) {
      setTimeout(scrollToBottom, 100);
    }
  }, [visible, scrollToBottom]);

  const handleHide = () => {
    setErrorMessage('');
    onHide();
    setChatPopupOpen(false);
    setChatData([]);
    setChatParticipants([]);
    formik.resetForm();
  };

  const headerGroupImage = senderImages[String(isMember ? 1 : chatInfoState?.groupID)] || noGroupImage;
  const headerGroupName = isMember 
    ? getChatUserName(1) 
    : (chatInfoState?.createdByGroupName || (chatInfoState?.groupID ? groupNamesData?.[chatInfoState.groupID]?.groupName : ''));

  return (
    <>
      {chatLoading && !isChatPopupOpen ? (
        <div className="loading chat-loading">
          <i className="pi pi-spin pi-spinner"></i>
        </div>
      ) : (
        <PR.Dialog
          blockScroll
          visible={visible}
          onHide={handleHide}
          className="logout-dialog response-dialog chat-dialog side-by-side"
          closeOnEscape="true"
        >
          <div className="dialog-header">
            <div className='flex align-items-center gap-2'>
              <PR.Image src={headerGroupImage} className="group-name-icon" />
              <div className='chat-header'>
                <div className=''>{`${headerGroupName} | `}Chat about Alert ID: <span className="capitalize">{chatInfoState?.legacyID}</span></div>
                <span className='alert-name'>{chatInfoState?.alertName}</span>
              </div>
            </div>
          </div>
          <div className="chat-side-by-side-container">
            {/* Left Column: Chat Editor Form */}
            <div className="chat-editor-column">
              <form autoComplete="off" onSubmit={formik.handleSubmit}>
                <span className="ask-field">
                  <div className="editor-wrapper">
                    <div className="p-editor-container">
                      <PR.Editor
                        placeholder="Message to send..."
                        name="content"
                        value={formik.values.content}
                        onTextChange={(e) => {
                          const htmlValue = e.htmlValue;
                          if (getPlainText(htmlValue) === 0 && htmlValue !== null) {
                            setErrorMessage('Enter valid text');
                          } else if (getPlainText(htmlValue) > chatCount) {
                            formik.setFieldValue('content', htmlValue);
                            setErrorMessage(`Message should be ${chatCount} characters or less.`);
                          } else {
                            formik.setFieldValue('content', htmlValue);
                            setErrorMessage("");
                          }
                        }}
                        modules={editorConfig.modules}
                        formats={editorConfig.formats}
                        className="chat-editor-style-1"
                      />
                      <div className='chat-editor-footer'>
                        {formik.values.attachments && (
                          <div className="uploaded-file-container flex flex-column gap-2">
                            {formik.values.attachments?.map((attachment, index) => (
                              <div className="uploaded-files" key={index}>
                                {trimFileName(attachment.name)}
                                <i className="pi pi-trash" onClick={() => handleDeleteAttachment(index)}/>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className='text-right'>
                          <PR.Button
                            icon="pi pi-send"
                            iconPos="right" 
                            title='Send Message'
                            outlined className={`dialog-logout-btns send-message ${isAnalyst ? 'analyst' : ''}`}
                            loading={chatLoading}
                            disabled={!(formik.values.content?.trim() || formik.values.attachments?.length > 0) || errorMessage || chatLoading}
                            type='submit'
                          />
                        </div> 
                      </div>
                    </div>
                  </div>
                  <div className="align-items-start justify-content-between mt-1">
                    <div className="descriptionGrid descriptioncharLimit">
                      <span style={{ textAlign: "left" }}>
                        {errorMessage && <div className="error-message">{errorMessage}</div>}
                      </span>
                      {remainingCharacters(chatCount, formik.values.content)}
                    </div>
                  </div>
                  <div className="flex flex-column gap-2 mt-3">
                    <div className="card file-upload flex align-items-center">
                      <PR.FileUpload
                        auto
                        mode="advanced"
                        name="attachments"
                        accept=".pdf,.csv,.docx,.xlsx,.doc,.xls,.png,.jpeg,.jpg"
                        chooseLabel="Choose File"
                        uploadLabel="Upload"
                        emptyTemplate={null}
                        itemTemplate={null}
                        customUpload
                        onSelect={(event) => {
                          const selectedFiles = event.files;
                          formik.setFieldTouched("attachments", true);
                          formik.setFieldValue("attachments", selectedFiles);
                        }}
                      />
                      {!formik.errors.attachments && (<span className="ml-2"> 
                        <span className="block file-format">(PDF, DOC, DOCX, XLS, XLSX, CSV, PNG, JPG or JPEG)</span>
                        {validationMessages.sizeExceed}.
                      </span>
                      )}
                      {formik.errors.attachments && (formik.touched.attachments || formik.submitCount > 0) && (
                        <div className="error-message ml-2">
                          {Array.isArray(formik.errors.attachments) ? (
                            Array.from(new Set(formik.errors?.attachments)).map((error, index) => (
                              <div key={index}>{error}</div>
                            ))
                          ) : (
                            <div>
                              {formik.errors.attachments}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </span>
              </form>
            </div>

            {/* Right Column: Chat Messages */}
            <div className="chat-messages-column">
              <div className="dialog-body-section">
                {chatInfoState?.isResponseChat ? (
                  chatInfoState?.responseDescription && (
                    <div className="chat-dialog-descriptions">
                      <div className="desc-item">
                        <div className="desc-label">Response</div>
                        <div className="desc-value" dangerouslySetInnerHTML={{ __html: dompurifyText(chatInfoState.responseDescription) }} />
                      </div>
                    </div>
                  )
                ) : (
                  chatInfoState?.alertDescription && (
                    <div className="chat-dialog-descriptions">
                      <div className="desc-item">
                        <div className="desc-label">Alert Description</div>
                        <div className="desc-value" dangerouslySetInnerHTML={{ __html: dompurifyText(chatInfoState.alertDescription) }} />
                      </div>
                    </div>
                  )
                )}
                {chatData?.length === 0 && !(chatInfoState?.isResponseChat ? chatInfoState?.responseDescription : chatInfoState?.alertDescription) ? (
                  <p className='no-message-p text-center'>No messages yet.</p>
                ) : (
                  chatData?.map((item, index) => {
                    const rendermessage = shouldDisplaySenderImage(item.senderID) ? (
                      <div className='chat-block-received' key={index}>
                        <div className='flex align-items-start'>
                          <div className='mr-3'>
                            <PR.Image src={senderImages[String(item.senderID)] || profileIcon} alt={getChatUserName(item.senderID) || 'User'} className="group-name-icon" />
                          </div>
                          <div className='message-box'>
                            <div className='message'>
                              {item?.content && <div className="content mb-2" dangerouslySetInnerHTML={{ __html: processHtmlLinks(item?.content) }} />}
                              {item?.attachment && (() => {
                                const attachment = JSON.parse(item.attachment); 
                                return (
                                  <PR.Button
                                    key={attachment.key}
                                    icon="pi pi-download"
                                    label={trimFileName(attachment.name)}
                                    className="file-download-button"
                                    onClick={() => fetchFileDetails(attachment)}
                                  />
                                );
                              })()}
                            </div>
                            <div className='time'>
                              <span>{dateTimeFormat(item.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className='chat-block-sent flex align-items-start' key={index}>
                        <div className='message-box'>
                          <div className='message'>
                            {item?.content && <div className="content mb-2" dangerouslySetInnerHTML={{ __html: processHtmlLinks(item?.content) }} />}
                            {item?.attachment && (() => {
                              const attachment = JSON.parse(item.attachment); 
                              return (
                                <PR.Button
                                  key={attachment.key}
                                  icon="pi pi-download"
                                  label={trimFileName(attachment.name)}
                                  className="file-download-button"
                                  onClick={() => fetchFileDetails(attachment)}
                                />
                              );
                            })()}
                          </div>
                          <div className='time text-right'>
                            <span>{dateTimeFormat(item.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    );
                    return <div key={index}>{rendermessage}</div>;
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>
        </PR.Dialog>
      )}
    </>
  );
};

export default ChatDialog;
